import { ReactNode } from "react";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import { CapabilitiesSection } from "@/components/Homepage/CapabilitiesSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useRevealObserver } from "@/hooks/useRevealObserver";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// --- Interfaces ---

export interface SolutionPageProps {
    // SEO
    seo: {
        title: string;
        description: string;
        keywords: string;
        canonical: string;
        structuredData?: any;
    };
    breadcrumbTitle?: string;

    // Hero Section
    hero: {
        badge: string;
        title: string;
        description: string;
        primaryCta: { text: string; link: string };
        secondaryCta: { text: string; link: string };
        gradientParams: string; // e.g. "from-teal-500/5 via-background to-background"
        badgeColor: string; // e.g. "text-teal-600"
        buttonColor: string; // e.g. "bg-teal-600 hover:bg-teal-700"
        image?: { src: string; alt: string }; // optional hero image (right column)
        visual?: ReactNode; // optional coded hero visual (right column) - preferred over image
    };

    // Problem Section
    problem: {
        title: string;
        description: ReactNode;
        cards: Array<{
            value: string;
            label: string;
            valueColor?: string; // e.g. "text-red-500"
            highlight?: boolean; // specialized styling for some cards
        }>;
        gridCols?: 2 | 3;
    };

    // System Model Section
    systemModel: {
        title: string;
        description: string;
        variant: "steps" | "cards";
        items: Array<{
            step?: string;
            title: string;
            description: string;
            icon?: any; // Lucide icon
            color?: string; // For "cards" variant borders/accents
        }>;
        connectionColor?: string; // For "steps" variant
    };

    // Layers Section
    layers: {
        title: string;
        description: string;
        items: Array<{
            title: string;
            icon: any;
            items: string[];
        }>;
        theme: "teal" | "indigo" | "blue" | "purple";
        sidebarContent?: ReactNode; // For WebSeo/CustomAI sidebar style
    };

    // Interdependence Section
    interdependence: {
        icon?: any; // For Crm/LeadGen style
        title: string;
        description?: ReactNode; // For Crm/LeadGen style
        quote?: string; // For Crm/LeadGen style
        cards?: Array<{ // For WebSeo/CustomAI style
            connectsTo: string;
            title: string;
            description: ReactNode;
        }>;
    };

    // Use Cases Section (Optional)
    useCases?: {
        title: string;
        items: Array<{
            title: string;
            description: string;
            link?: string;
        }>;
    };

    // Social Proof Section
    socialProof: {
        icon: any;
        title: string;
        quote: string;
        ctaLink: string;
        ctaText?: string;
    };

    // Final CTA Section
    cta: {
        title: string;
        description: string;
        primaryCta: { text: string; link: string };
        secondaryCta: { text: string; link: string };
        color?: string; // button color override
    };

    // Optional off-ramp banner rendered between hero and the rest of the page.
    // Use to redirect visitors who landed on a page above their current stage.
    offRamp?: {
        label?: string; // bolded lead-in; defaults to "Not there yet?" (down-sell) - set e.g. "Take it further." for an up-sell
        message: string;
        ctaText: string;
        ctaLink: string;
        accentColor?: string; // Tailwind text color, e.g. "text-blue-600"
    };

    // Optional local-proof story rendered between hero and problem section.
    // Use to anchor the page in a real, named local client outcome before stats.
    localStory?: {
        eyebrow?: string;
        client: string;
        location?: string;
        story: ReactNode;
        result?: string;
        accentColor?: string; // Tailwind text color, e.g. "text-teal-600"
    };

    // Custom Section injection (e.g. for Productized Offers)
    customSection?: ReactNode;
}

const DEFAULT_LAYERS: SolutionPageProps["layers"] = {
    title: "",
    description: "",
    items: [],
    theme: "indigo"
};

export const SolutionPage = (props: SolutionPageProps) => {
    useRevealObserver();

    const {
        seo = {} as SolutionPageProps['seo'],
        hero = {} as SolutionPageProps['hero'],
        problem = {} as SolutionPageProps['problem'],
        systemModel = {} as SolutionPageProps['systemModel'],
        layers = DEFAULT_LAYERS,
        interdependence = {} as SolutionPageProps['interdependence'],
        useCases,
        socialProof = {} as SolutionPageProps['socialProof'],
        cta = {} as SolutionPageProps['cta'],
        offRamp,
        localStory,
        customSection,
        breadcrumbTitle
    } = props;

    // Generate Breadcrumb Schema
    const pageTitle = breadcrumbTitle || hero.title;
    const breadcrumbSchema = {
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://frayze.ca/"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "Solutions",
                "item": "https://frayze.ca/solutions/"
            },
            {
                "@type": "ListItem",
                "position": 3,
                "name": pageTitle,
                "item": seo.canonical
            }
        ]
    };

    // Merge structured data
    const finalStructuredData = {
        "@context": "https://schema.org",
        "@graph": [
            breadcrumbSchema,
            ...(seo.structuredData ? (Array.isArray(seo.structuredData) ? seo.structuredData : [seo.structuredData]) : [])
        ]
    };



    // Helper to get theme colors
    const getThemeColors = (theme: string) => {
        switch (theme) {
            case "teal": return { bg: "bg-[#FBFAF7]", border: "border-[#E9E6DE]", hover: "hover:border-[#2F5FE8]", iconBg: "bg-[#F4F2EC]", iconText: "text-[#2F5FE8]", check: "text-[#1B9E6B]" };
            case "indigo": return { bg: "bg-[#FBFAF7]", border: "border-[#E9E6DE]", hover: "hover:border-[#2F5FE8]", iconBg: "bg-[#F4F2EC]", iconText: "text-[#2F5FE8]", check: "text-[#1B9E6B]" };
            case "blue": return { bg: "bg-[#FBFAF7]", text: "text-[#101319]", badge: "bg-[#F4F2EC] text-[#2F5FE8]", iconBg: "bg-[#F4F2EC]", iconText: "text-[#2F5FE8]", border: "border-[#E9E6DE]", subBadge: "bg-[#F4F2EC] text-[#5A6270]" };
            case "purple": return { bg: "bg-[#FBFAF7]", text: "text-[#101319]", badge: "bg-[#F4F2EC] text-[#2F5FE8]", iconBg: "bg-[#F4F2EC]", iconText: "text-[#2F5FE8]", border: "border-[#E9E6DE]", subBadge: "bg-[#F4F2EC] text-[#5A6270]" };
            default: return { bg: "bg-[#FBFAF7]", border: "border-[#E9E6DE]", hover: "hover:border-[#2F5FE8]", iconBg: "bg-[#F4F2EC]", iconText: "text-[#2F5FE8]", check: "text-[#1B9E6B]" };
        }
    };

    const themeColors = getThemeColors(layers.theme);

    return (
        <div className="min-h-screen bg-[#FBFAF7] text-[#101319] font-sans antialiased overflow-x-hidden">
            <SEOHead {...seo} structuredData={finalStructuredData} />
            <Header />

            <main>
                <div className="max-w-[1312px] mx-auto px-6 py-4 relative z-20 pt-32">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/" className="text-[#5A6270] hover:text-[#101319]">Home</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator className="text-[#C9C4B8]" />
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/solutions/" className="text-[#5A6270] hover:text-[#101319]">Solutions</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator className="text-[#C9C4B8]" />
                            <BreadcrumbItem>
                                <BreadcrumbPage className="text-[#101319] font-bold">{pageTitle}</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>

                {/* HERO SECTION */}
                <section className="relative py-20 md:py-28 overflow-hidden bg-[#101319] text-white">
                    {/* Legacy per-page gradientParams intentionally ignored: one hero treatment site-wide (readability fix 2026-07-10) */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#2F5FE8]/10 to-transparent" />
                    <div className="max-w-[1312px] mx-auto px-6 relative z-10">
                        <div className={cn((hero?.image || hero?.visual) && "grid md:grid-cols-2 gap-10 lg:gap-16 items-center")}>
                            <div className={(hero?.image || hero?.visual) ? "text-left" : "text-center"}>
                                <span className="inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase mb-6 text-[#7EA0F5]">
                                    <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
                                    {hero.badge}
                                </span>
                                <h1 className="text-4xl sm:text-5xl md:text-[68px] lg:text-[72px] leading-[1.02] tracking-[-0.04em] font-semibold text-white text-pretty mb-6">
                                    {hero.title}
                                </h1>
                                <p className={cn("text-lg md:text-xl leading-relaxed text-[#B9C1CE] text-pretty mb-10", (hero?.image || hero?.visual) ? "max-w-2xl" : "max-w-2xl mx-auto")}>
                                    {hero.description}
                                </p>
                                <div className={cn("flex flex-col sm:flex-row gap-4", (hero?.image || hero?.visual) ? "justify-start" : "justify-center")}>
                                    {hero?.primaryCta && (
                                        <Link to={hero.primaryCta.link || "#"} className="text-center font-bold px-8 py-4 rounded-xl shadow-lg shadow-[#2F5FE8]/25 transition-all hover:scale-[1.02] active:scale-[0.98] bg-[#2F5FE8] hover:bg-[#2049BE] text-white">
                                            {hero.primaryCta.text}
                                        </Link>
                                    )}
                                    {hero?.secondaryCta && (
                                        <Link to={hero.secondaryCta.link || "#"} className="text-center border border-[#D8D4C9] bg-transparent hover:border-white text-white font-bold px-8 py-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]">
                                            {hero.secondaryCta.text}
                                        </Link>
                                    )}
                                </div>
                            </div>
                            {(hero?.visual || hero?.image) && (
                                <div className="flex justify-center md:justify-end">
                                    {hero?.visual ? hero.visual : (
                                        <img
                                            src={hero.image?.src}
                                            alt={hero.image?.alt}
                                            width={460}
                                            height={460}
                                            loading="eager"
                                            className="w-full max-w-[300px] md:max-w-[460px] h-auto"
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* OFF-RAMP BANNER (optional): pivot visitors who landed above their stage */}
                {offRamp && (
                    <section className="border-y border-[#E9E6DE] bg-[#F4F2EC]">
                        <div className="max-w-[1312px] mx-auto px-6 py-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-[#E9E6DE] bg-white px-5 py-4 shadow-sm text-center sm:text-left">
                                <p className="text-sm md:text-base text-[#5A6270]">
                                    <span className={cn("font-bold text-[#101319]", offRamp.accentColor || "text-[#2F5FE8]")}>
                                        {offRamp.label || "Not there yet?"}
                                    </span>{" "}
                                    {offRamp.message}
                                </p>
                                <Link
                                    to={offRamp.ctaLink}
                                    className={cn(
                                        "inline-flex items-center gap-1.5 text-sm font-bold whitespace-nowrap transition-all hover:gap-2.5",
                                        offRamp.accentColor || "text-[#2F5FE8]"
                                    )}
                                >
                                    {offRamp.ctaText}
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </section>
                )}

                {/* LOCAL STORY SECTION (optional): anchors the page in a real local outcome before stats */}
                {localStory && (
                    <section className="border-y border-[#E9E6DE] bg-white py-16 md:py-24">
                        <div className="max-w-[1312px] mx-auto px-6 max-w-3xl">
                            <div className="reveal relative overflow-hidden rounded-2xl border border-[#E9E6DE] bg-white p-8 md:p-12 shadow-sm lift-hover">
                                {/* accent rail */}
                                <div className={cn("absolute inset-y-0 left-0 w-1", (localStory.accentColor || "text-[#2F5FE8]").replace("text-", "bg-"))} />
                                {localStory.eyebrow && (
                                    <p className={cn("mb-4 text-xs font-bold uppercase tracking-[0.16em] font-mono-ibm", localStory.accentColor || "text-[#2F5FE8]")}>
                                        {localStory.eyebrow}
                                    </p>
                                )}
                                <div className="text-xl md:text-2xl font-medium leading-relaxed text-[#101319]">
                                    {localStory.story}
                                </div>
                                {localStory.result && (
                                    <p className={cn("mt-5 text-lg md:text-xl font-bold", localStory.accentColor || "text-[#1B9E6B]")}>
                                        {localStory.result}
                                    </p>
                                )}
                                <div className="mt-7 flex items-center gap-3 border-t border-[#E9E6DE] pt-5">
                                    <div className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-bold text-white", (localStory.accentColor || "text-[#2F5FE8]").replace("text-", "bg-"))}>
                                        {(localStory.client || "F").charAt(0)}
                                    </div>
                                    <div className="text-sm">
                                        <span className="font-bold text-[#101319]">{localStory.client}</span>
                                        {localStory.location && <span className="text-[#5A6270]"> &middot; {localStory.location}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* PROBLEM SECTION */}
                <section className="py-20 md:py-28 bg-white border-y border-[#E9E6DE]">
                    <div className="max-w-[1312px] mx-auto px-6 max-w-3xl text-center">
                        <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold mb-6 text-[#101319]">{problem.title}</h2>
                        <div className="reveal reveal-delay-2 text-base md:text-lg text-[#5A6270] leading-relaxed mb-12">
                            {problem.description}
                        </div>
                        <div className={cn("grid gap-6 text-left", problem.gridCols === 2 ? "sm:grid-cols-2 max-w-2xl mx-auto" : "sm:grid-cols-3")}>
                            {(problem.cards || []).map((card, i) => (
                                <div key={i} className={cn("reveal bg-white p-6 rounded-2xl border border-[#E9E6DE] transition-all hover:shadow-lg hover:border-[#2F5FE8]/50 lift-hover", i % 4 === 0 ? "" : i % 4 === 1 ? "reveal-delay-1" : i % 4 === 2 ? "reveal-delay-2" : "reveal-delay-3")}>
                                    <div className={cn("text-4xl font-bold mb-3", card.valueColor || "text-[#1B9E6B]")}>
                                        {card.value}
                                    </div>
                                    <p className="text-sm text-[#5A6270]">{card.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* SYSTEM MODEL SECTION */}
                <section className="py-20 md:py-28 bg-[#F4F2EC]">
                    <div className="max-w-[1312px] mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold mb-4 text-[#101319]">{systemModel.title}</h2>
                            <p className="reveal reveal-delay-2 text-base md:text-lg text-[#5A6270]">{systemModel.description}</p>
                        </div>

                        {systemModel.variant === "steps" ? (
                            <div className="relative">
                                {/* Connection Line */}
                                <div className={cn("hidden md:block absolute top-[2.25rem] left-0 w-full h-1 z-0", systemModel.connectionColor || "bg-[#2F5FE8]/20")} />

                                <div className="grid md:grid-cols-4 gap-8">
                                    {(systemModel.items || []).map((item, i) => (
                                        <div key={i} className={cn("reveal relative z-10 font-bold flex flex-col items-center text-center group md:text-left md:items-start", i % 4 === 0 ? "" : i % 4 === 1 ? "reveal-delay-1" : i % 4 === 2 ? "reveal-delay-2" : "reveal-delay-3")}>
                                            {item.icon ? (
                                                // Icon style (WebSeo)
                                                <div className="w-20 h-20 rounded-2xl bg-white border-2 border-[#E9E6DE] group-hover:border-[#2F5FE8] transition-colors flex items-center justify-center mb-6 shadow-sm">
                                                    <span className="text-2xl font-bold text-[#2F5FE8]">{item.step}</span>
                                                </div>
                                            ) : (
                                                // Number circle style (CrmGrowth)
                                                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white border-4 border-[#2F5FE8] flex items-center justify-center text-[#2F5FE8] font-bold text-xl mx-auto md:mx-0 mb-4 md:mb-6 shadow-sm">
                                                    {item.step}
                                                </div>
                                            )}

                                            <h3 className="text-xl font-bold mb-2 text-[#101319]">{item.title}</h3>
                                            <p className="text-[#5A6270] text-sm">{item.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            // Cards variant (LeadGen)
                            <div className="grid md:grid-cols-3 gap-8">
                                {(systemModel.items || []).map((item, i) => {
                                    const Icon = item.icon;
                                    return (
                                        <div key={i} className={cn("reveal p-8 rounded-2xl border border-[#E9E6DE] bg-white shadow-sm hover:shadow-lg transition-all relative overflow-hidden group lift-hover", i % 3 === 0 ? "" : i % 3 === 1 ? "reveal-delay-1" : "reveal-delay-2")}>
                                            <div className={cn("absolute top-0 left-0 w-full h-1 bg-[#2F5FE8]")}></div>
                                            {Icon && <Icon className="w-12 h-12 mx-auto mb-6 text-[#2F5FE8]" />}
                                            <h3 className="text-xl font-bold mb-3 text-[#101319]">{item.title}</h3>
                                            <p className="text-[#5A6270] text-sm leading-relaxed">
                                                {item.description}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </section>

                {/* LAYERS SECTION */}
                <section className={cn("py-20 md:py-28", themeColors.bg, themeColors.text)}>
                    <div className="max-w-[1312px] mx-auto px-6">
                        {layers.sidebarContent ? (
                            // Sidebar Layout (WebSeo style)
                            <div className="grid md:grid-cols-2 gap-16 items-center">
                                <div>
                                    <div className={cn("reveal inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase mb-6 px-3 py-1.5 rounded-full", themeColors.badge)}>
                                        <span className="w-4 h-[1px] bg-[#2F5FE8]"></span>
                                        System Layers
                                    </div>
                                    <h2 className="reveal reveal-delay-1 text-3xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold mb-6 text-[#101319]">{layers.title}</h2>
                                    <p className="reveal reveal-delay-2 text-lg mb-8 leading-relaxed text-[#5A6270]">
                                        {layers.description}
                                    </p>
                                    <div className="space-y-6">
                                        {layers.items.map((layer, i) => {
                                            const Icon = layer.icon;
                                            return (
                                                <div key={i} className={cn("reveal flex gap-4", i % 3 === 0 ? "" : i % 3 === 1 ? "reveal-delay-1" : "reveal-delay-2")}>
                                                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center shrink-0", themeColors.iconBg)}>
                                                        <Icon className={cn("w-6 h-6", themeColors.iconText)} />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-lg font-bold mb-2 text-[#101319]">{layer.title}</h4>
                                                        <div className="flex flex-wrap gap-2">
                                                            {layer.items.map((item, j) => (
                                                                <span key={j} className={cn("text-xs px-2.5 py-1 rounded-full border", themeColors.subBadge, themeColors.border)}>
                                                                    {item}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {layers.sidebarContent}
                            </div>
                        ) : (
                            // Grid Layout (CrmGrowth/LeadGen style)
                            <>
                                <div className="text-center mb-16">
                                    <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold mb-4 text-[#101319]">{layers.title}</h2>
                                    <p className="reveal reveal-delay-2 text-base md:text-lg text-[#5A6270]">{layers.description}</p>
                                </div>
                                <div className="grid md:grid-cols-2 gap-6">
                                    {layers.items.map((layer, index) => {
                                        const Icon = layer.icon;
                                        return (
                                            <div key={index} className={cn("reveal border rounded-2xl p-6 md:p-8 transition-all hover:shadow-lg hover:border-[#2F5FE8]/50 lift-hover", themeColors.border, "bg-white", index % 2 === 0 ? "" : "reveal-delay-1")}>
                                                <div className="flex items-center gap-4 mb-4 pb-2 border-b border-[#E9E6DE]">
                                                    <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center", themeColors.iconBg, themeColors.iconText)}>
                                                        <Icon className="h-5 w-5" />
                                                    </div>
                                                    <h3 className="text-lg font-bold text-[#101319]">{layer.title}</h3>
                                                </div>
                                                <ul className="space-y-3">
                                                    {layer.items.map((item, idx) => (
                                                        <li key={idx} className="flex items-start gap-3 text-sm text-[#5A6270]">
                                                            <CheckCircle2 className={cn("h-4 w-4 mt-0.5 shrink-0", themeColors.check)} />
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </section>

                {customSection}

                <CapabilitiesSection className="py-20 md:py-28 bg-[#F4F2EC]" eyebrow="THE CONNECTED SYSTEM" title="Everything Frayze automates for you" subtitle="This layer plugs into one connected system. Here’s the full set of capabilities working behind the scenes." />

                {/* INTERDEPENDENCE SECTION */}
                <section className="py-20 md:py-28 bg-[#101319] text-white text-center">
                    <div className="max-w-[1312px] mx-auto px-6">
                        {interdependence.icon && (
                            <interdependence.icon className="reveal w-12 h-12 mx-auto mb-6 text-[#2F5FE8]" />
                        )}

                        <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold mb-6">{interdependence.title}</h2>

                        {interdependence.description && (
                            <div className="reveal reveal-delay-2 text-lg md:text-xl leading-relaxed mb-8 text-[#8A8FA0] max-w-3xl mx-auto">
                                {interdependence.description}
                            </div>
                        )}

                        {interdependence.quote && (
                            <div className="reveal reveal-delay-2 inline-block px-6 py-3 rounded-full bg-white/10 border border-white/20 text-sm font-semibold text-white">
                                {interdependence.quote}
                            </div>
                        )}

                        {interdependence.cards && (
                            <div className="grid md:grid-cols-3 gap-8 mt-12">
                                {(interdependence.cards || []).map((card, i) => (
                                    <div key={i} className={cn("reveal p-6 rounded-2xl border border-[#E9E6DE]/20 bg-white/5 backdrop-blur-sm lift-hover", i % 3 === 0 ? "" : i % 3 === 1 ? "reveal-delay-1" : "reveal-delay-2")}>
                                        <div className="text-left mb-4">
                                            <span className="text-xs font-bold text-[#8A8FA0] uppercase">{card.connectsTo}</span>
                                            <h3 className="text-lg font-bold text-white mt-2">{card.title}</h3>
                                        </div>
                                        <p className="text-sm text-[#8A8FA0] text-left">{card.description}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* USE CASES SECTION */}
                {useCases && (
                    <section className="py-20 md:py-28 bg-white">
                        <div className="max-w-[1312px] mx-auto px-6">
                            <div className="text-center mb-16">
                                <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">{useCases.title}</h2>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-6">
                                {useCases.items.map((caseItem, i) => (
                                    <div key={i} className={cn("reveal p-6 md:p-8 rounded-2xl bg-[#FBFAF7] border border-[#E9E6DE] hover:border-[#2F5FE8]/50 hover:shadow-lg transition-all lift-hover", i % 3 === 0 ? "" : i % 3 === 1 ? "reveal-delay-1" : "reveal-delay-2")}>
                                        <h3 className="font-bold text-lg text-[#101319] mb-2">{caseItem.title}</h3>
                                        <p className="text-sm text-[#5A6270]">{caseItem.description}</p>
                                        {caseItem.link ? (
                                            <Link to={caseItem.link} className="text-[#2F5FE8] text-sm font-bold mt-4 inline-flex items-center gap-1 hover:gap-2 transition-all">
                                                See Use Case <ArrowRight className="w-3.5 h-3.5" />
                                            </Link>
                                        ) : (
                                            <Link to="/case-studies/" className="text-[#2F5FE8] text-sm font-bold mt-4 inline-flex items-center gap-1 hover:gap-2 transition-all">
                                                See Use Case <ArrowRight className="w-3.5 h-3.5" />
                                            </Link>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* SOCIAL PROOF SECTION */}
                <section className="py-20 md:py-28 bg-[#F4F2EC] border-y border-[#E9E6DE]">
                    <div className="max-w-[1312px] mx-auto px-6 max-w-4xl text-center">
                        <div className="mb-8">
                            <socialProof.icon className="reveal w-12 h-12 mx-auto mb-6 text-[#1B9E6B]" />
                            <h2 className="reveal reveal-delay-1 text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold mb-4 text-[#101319]">{socialProof.title}</h2>
                            <p className="reveal reveal-delay-2 text-lg text-[#5A6270] leading-relaxed max-w-2xl mx-auto">{socialProof.quote}</p>
                        </div>
                        <Link to={socialProof.ctaLink} className="reveal reveal-delay-2 inline-flex items-center gap-2 bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold px-8 py-4 rounded-xl transition-all hover:scale-[1.02]">
                            {socialProof.ctaText || "Read Full Case Study"}
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </section>

                {/* CTA SECTION */}
                <section className="py-24 md:py-32 bg-[#101319] text-white">
                    <div className="max-w-[1312px] mx-auto px-6 text-center max-w-3xl">
                        <h2 className="reveal reveal-delay-1 text-3xl md:text-5xl font-bold mb-6">{cta.title}</h2>
                        <p className="reveal reveal-delay-2 text-lg text-[#8A8FA0] mb-10">
                            {cta.description}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            {cta?.primaryCta && (
                                <Link to={cta.primaryCta.link || "#"} className={cn("text-center font-bold px-8 py-4 rounded-xl shadow-lg transition-all hover:scale-[1.02]", cta.color || "bg-[#2F5FE8] hover:bg-[#2049BE] text-white")}>
                                    {cta.primaryCta.text}
                                </Link>
                            )}
                            {cta?.secondaryCta && (
                                <Link to={cta.secondaryCta.link || "#"} className="text-center border border-white/20 hover:border-white/50 text-white font-bold px-8 py-4 rounded-xl transition-all hover:scale-[1.02]">
                                    {cta.secondaryCta.text} <ArrowRight className="inline ml-2 h-4 w-4" />
                                </Link>
                            )}
                        </div>
                        <p className="mt-8 text-sm text-[#8A8FA0]">
                            Looking for pricing? <Link to="/pricing/" className="text-white font-bold underline hover:no-underline">View Plans</Link>
                        </p>
                    </div>
                </section>

            </main>

            <Footer />
        </div>
    );
};
